import { pickT } from "@repo/shared";
import { useTheme } from "next-themes";
import { useEffect, useMemo, useState } from "react";
import { VegaEmbed } from "react-vega";
import { useContainerWidth } from "@/lib/hooks/use-container-width";
import { useUserLanguage } from "@/lib/i18n/context";
import type { MultiLineData } from "@/lib/utils/vega-chart-shared";
import { T } from "./multi-bar-chart.i18n";
import { createMultiBarChartSpec } from "./vega-specs";

type MultiBarChartProps = {
  data: MultiLineData[];
  width?: number | "container";
  height?: number;
  title?: string;
  line2Label?: string; // Net "with cost" value (bottom segment)
  costLabel?: string; // Cost drag (top segment)
  yAxisLabel?: string;
  primaryColor?: string;
  lang?: string;
};

/**
 * Client-side stacked-bar chart (Vega-Lite) — the "bar" rendering option for
 * cost charts: net value (bottom) + cost drag (top), totalling the without-cost
 * value. Same data shape and palette as {@link MultiLineChart}.
 */
export function MultiBarChart({
  data,
  width = "container",
  height = 300,
  title,
  line2Label = "With Cost",
  costLabel = "Cost",
  yAxisLabel,
  primaryColor,
  lang,
}: MultiBarChartProps) {
  const { userLanguage } = useUserLanguage();
  // One language for the labels and the Vega tooltips.
  const chartLang = lang ?? userLanguage;
  const t = pickT(T, chartLang);
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  // When width is "container", measure the wrapper and render at that pixel
  // width so the chart fills its column / full-width slot (see useContainerWidth).
  const [containerRef, containerWidth] = useContainerWidth<HTMLDivElement>();
  const effectiveWidth = typeof width === "number" ? width : containerWidth;

  useEffect(() => {
    setMounted(true);
  }, []);

  const isDark = mounted && resolvedTheme === "dark";

  const spec = useMemo(() => {
    if (!data || data.length === 0 || effectiveWidth === null) {
      return null;
    }

    const chartSpec = createMultiBarChartSpec(data, {
      width: effectiveWidth,
      height,
      title,
      line2Label,
      costLabel,
      yAxisLabel,
      isDark,
      primaryColor,
      lang: chartLang,
    });

    return JSON.parse(JSON.stringify(chartSpec));
  }, [
    data,
    effectiveWidth,
    height,
    title,
    line2Label,
    costLabel,
    yAxisLabel,
    isDark,
    primaryColor,
    chartLang,
  ]);

  if (!data || data.length === 0) {
    return (
      <div className="w-full p-6 text-center">
        <p className="text-sm text-muted-foreground">{t.noData}</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="w-full [&_svg]:max-w-full [&_svg]:h-auto"
      style={{ minHeight: height }}
    >
      {spec && (
        <VegaEmbed
          spec={spec}
          options={{
            actions: false,
            renderer: "svg",
          }}
        />
      )}
    </div>
  );
}
